import * as React from 'react'
import Game from '../components/Game'
import CardModel from '../models/Card'
import Exit from '../models/cards/Exit'
import Gold from '../models/cards/Gold'
import Monster, { MonsterAbility } from '../models/cards/Monster'
import Player from '../models/cards/Player'
import Potion from '../models/cards/Potion'
import Spell from '../models/cards/Spell'
import GameBoardModel from '../models/GameBoard'
import Hero from '../models/Hero'
import MonsterFactory from '../models/MonsterFactory'

interface ICardProps {
  model: CardModel
  className?: string
  onClick?(): void
}

export default class Card extends React.Component<ICardProps> {
  card: CardModel
  loot: CardModel

  constructor(props: ICardProps) {
    super(props)
    this.card = this.props.model
    this.loot = this.card instanceof Monster && this.card.loot
  }

  getStyle() {
    if (!this.card.location) return {}

    const w = 100
    const h = 100
    const p = 0
    const d = 20
    const deck = this.card.board.deckAt(this.card.location.x, this.card.location.y)
    const m = -deck.size * d
    const minDeckSize = this.card.board.minDeckSize()

    return {
      left: this.card.location.x * (w + p) + d,
      top: this.card.location.y * (h + p) + (m / d - 1) * d + minDeckSize * 20,
      zIndex: (this.card.location.y + 1) * 10
    }
  }

  getLootStyle() {
    const s = this.getStyle()
    if (!s.zIndex) {
      return {
        top: 30,
        left: 100 * (1 + this.card.board?.hero.spells.cards.indexOf(this.card) || 0) + 36
      }
    }
    return {
      left: s.left! + 100 + 15,
      top: s.top! - 15,
      zIndex: s.zIndex + 1
    }
  }

  getTooltipStyle() {
    const s = this.getStyle()
    if (!s.zIndex) {
      return {
        top: 50,
        left: 130 * (this.card.board?.hero.spells.cards.indexOf(this.card) || 0)
      }
    }
    return s
  }

  getMainEmoji(card = this.card) {
    if (card instanceof Player) return '🦸'
    else if (card instanceof Exit) return '🚪'
    else if (card instanceof Monster) return MonsterFactory.getEmojiFor(this.card.name)
    else if (card instanceof Potion) return '🧃'
    else if (card instanceof Gold) return '💰'
    else if (card instanceof Spell) return '✨'
  }

  getValueEmoji() {
    if (this.card instanceof Player) return '💖'
    else if (this.card instanceof Exit) return ''
    else if (this.card instanceof Spell) return ''
    else if (this.card instanceof Monster) return '❤️'
    else if (this.card instanceof Potion) return '💚'
    else if (this.card instanceof Gold) return '💰'
  }

  onClick() {
    GameBoardModel.clearError()
    if (this.card instanceof Monster && this.card.canSpellbind)
      this.card.spellbind()

    Game.refresh()
  }

  getLabel() {
    if (
      this.card instanceof Player ||
      this.card instanceof Gold ||
      this.card instanceof Potion ||
      this.card instanceof Monster
    )
      return `${this.getValueEmoji()}${this.card.value}`

    return this.card.name
  }

  getTooltipPosition() {
    if (!this.card.location) return 'top'
    if (this.card.location.x === 0) return 'right'
    if (this.card.location.x === this.card.board!.width - 1) return 'left'
    if (this.card.location.y === this.card.board!.height - 1) return 'top'
    return 'bottom'
  }

  describeAbilities() {
    if (!(this.card instanceof Monster)) return ''
    return this.card.abilities.map(ability => {
      switch (ability) {
        case MonsterAbility.Persistant:
          return <p key={ability}><strong>Persistant:</strong> Cannot be put back in any deck.</p>
        case MonsterAbility.Greedy:
          return <p key={ability}><strong>Greedy:</strong> Value of the monster doesn't decrease on sliding.</p>
        case MonsterAbility.Constant:
          return <p key={ability}><strong>Constant:</strong> Value of the monster always stays the same on sliding.</p>
        case MonsterAbility.Copycat:
          return <p key={ability}><strong>Copycat:</strong> Copies the highest nearby card value.</p>
        case MonsterAbility.GoldDigger:
          return <p key={ability}><strong>Gold Digger:</strong> Every time you pick up gold, the value of the monster goes up.</p>
        case MonsterAbility.Infector:
          return <p key={ability}><strong>Infector:</strong> Infects one adjacent card on each move you make. (Infect: Converts loot to a monster of this type.)</p>
        case MonsterAbility.Multiplier:
          return <p key={ability}><strong>Multiplier:</strong> If there is an empty tile nearby, on each move you make, copies itself to that tile.</p>
        case MonsterAbility.Shy:
          return <p key={ability}><strong>Shy:</strong> On appear, puts itself to the bottom of the deck it came from.</p>
        case MonsterAbility.PoisonNearby:
          return <p key={ability}><strong>Poison:</strong> On pickup, poisons all adjacent monsters. (Poison: value reduces by 1 on each move you make.)</p>
      }
    })
  }

  getType(card: CardModel = this.card) {
    if (card instanceof Player) return 'hero'
    else if (card instanceof Exit) return 'exit'
    else if (card instanceof Monster) return 'monster'
    else if (card instanceof Potion) return 'potion'
    else if (card instanceof Gold) return 'gold'
    else if (card instanceof Spell) return 'spell'
  }

  getValue(card: CardModel = this.card) {
    return typeof (card as any).value === 'number' ? (card as any).value : ''
  }

  isSpellbound() {
    if (this.card instanceof Monster)
      return this.card.isSpellbound()
  }

  render() {
    return (
      <div>
        <div className={`absolute card ${this.getType()} ${this.props.className || ''} `}
          style={this.getStyle()}
          onClick={() => {
            if (typeof this.props.onClick === 'function')
              this.props.onClick()
            this.onClick()
          }}>
          <h1>{this.getMainEmoji()}</h1>
          {this.getLabel()}
        </div>
        {this.loot && <div className={`badge loot loot-${this.getType(this.loot)}`} style={this.getLootStyle()}>
          {this.getMainEmoji(this.loot)}
          <small>{this.getValue(this.loot)}</small>
        </div>}
        {this.isSpellbound() && <div className={`badge spellbound`} style={this.getLootStyle()}>
          🔮
        </div>}
        <div style={this.getTooltipStyle()} className={`tooltip ${this.getTooltipPosition()}`}>
          <h3>
            {this.card.name}
            {this.getValueEmoji()}
            {this.getValue()}
          </h3>
          {this.describeAbilities()}
        </div>
      </div>
    )
  }
}
